
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function JournalPreview() {
  const [currentPage, setCurrentPage] = useState(0);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const journalPages = [
    {
      title: "Welcome to Your Dream Journal",
      type: "intro",
      content: `This journal is your sacred space for capturing divine inspiration and transforming dreams into reality. Here, you'll discover your God-given purpose and create a roadmap for meaningful achievement.

"For I know the plans I have for you," declares the Lord, "plans to prosper you and not to harm you, to give you hope and a future." - Jeremiah 29:11

Your dreams are not accidents—they are divine appointments waiting to be fulfilled.`
    },
    {
      title: "Dream Discovery Exercise",
      type: "exercise",
      content: `Take a moment to reflect on these questions:

1. What dreams have been stirring in your heart lately?
2. When do you feel most alive and purposeful?
3. What would you attempt if you knew you couldn't fail?
4. How might God be calling you to serve others?

Write your thoughts below:`,
      hasWritingSpace: true
    },
    {
      title: "Vision Mapping Template",
      type: "template",
      content: `Create your personal vision map using this framework:

MY CORE VALUES:
• Faith & Spiritual Growth
• Family & Relationships  
• Personal Excellence
• Service to Others

MY 5-YEAR VISION:
[Space for your vision]

MY 1-YEAR GOALS:
[Space for your goals]

MY NEXT 90 DAYS:
[Space for immediate actions]`,
      hasWritingSpace: true
    },
    {
      title: "Daily Reflection Pages",
      type: "daily",
      content: `Date: ___________

MORNING INTENTION:
What is God calling me to focus on today?

GRATITUDE:
Three things I'm grateful for:
1. ________________
2. ________________  
3. ________________

EVENING REFLECTION:
How did I grow today?

What did I learn?

Tomorrow's priority:`,
      hasWritingSpace: true
    },
    {
      title: "Action Planning Worksheet",
      type: "planning",
      content: `DREAM: _________________________

OBSTACLES I MIGHT FACE:
• ________________
• ________________
• ________________

RESOURCES I NEED:
• ________________
• ________________
• ________________

FIRST THREE STEPS:
1. ________________
2. ________________
3. ________________

ACCOUNTABILITY PARTNER:
________________

TARGET COMPLETION DATE:
________________`,
      hasWritingSpace: true
    },
    {
      title: "Scripture & Inspiration",
      type: "inspiration",
      content: `"Commit to the Lord whatever you do, and he will establish your plans." - Proverbs 16:3

"She is clothed with strength and dignity; she can laugh at the days to come." - Proverbs 31:25

"And we know that in all things God works for the good of those who love him, who have been called according to his purpose." - Romans 8:28

PERSONAL REFLECTION:
Which scripture speaks to your heart today?

How can you apply this wisdom to your current dreams?`,
      hasWritingSpace: true
    }
  ];

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('source', 'journal_preview');

      const response = await fetch('https://readdy.ai/api/form/d3iinjd4ikgvg63iuelg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => {
          setShowEmailPopup(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % journalPages.length);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + journalPages.length) % journalPages.length);
  };

  const currentJournalPage = journalPages[currentPage];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-yellow-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-slate-800 mb-6">The Dream Journal Preview</h1>
          <p className="text-xl text-slate-600 mb-8">
            Experience the transformative power of guided reflection and purposeful planning. 
            This preview shows you exactly what's inside your journey to discovering divine purpose.
          </p>
          <div className="flex justify-center">
            <Link to="/shop">
              <Button size="lg" className="bg-blue-800 hover:bg-blue-900">
                Get Your Copy Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Journal Preview */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Journal Header */}
            <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">The Dream Journal</h2>
                  <p className="opacity-90">Page {currentPage + 1} of {journalPages.length}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={prevPage}
                    className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line text-xl"></i>
                  </button>
                  <button
                    onClick={nextPage}
                    className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-xl"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Journal Content */}
            <div className="p-8 min-h-[600px]">
              <div className="mb-6">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-4 ${
                  currentJournalPage.type === 'intro' ? 'bg-blue-100 text-blue-800' :
                  currentJournalPage.type === 'exercise' ? 'bg-green-100 text-green-800' :
                  currentJournalPage.type === 'template' ? 'bg-purple-100 text-purple-800' :
                  currentJournalPage.type === 'daily' ? 'bg-yellow-100 text-yellow-800' :
                  currentJournalPage.type === 'planning' ? 'bg-red-100 text-red-800' :
                  'bg-indigo-100 text-indigo-800'
                }`}>
                  {currentJournalPage.type === 'intro' ? 'Introduction' :
                   currentJournalPage.type === 'exercise' ? 'Reflection Exercise' :
                   currentJournalPage.type === 'template' ? 'Planning Template' :
                   currentJournalPage.type === 'daily' ? 'Daily Pages' :
                   currentJournalPage.type === 'planning' ? 'Action Planning' :
                   'Inspiration'}
                </span>
              </div>

              <h3 className="text-3xl font-bold text-slate-800 mb-6">{currentJournalPage.title}</h3>
              
              <div className="prose prose-lg max-w-none">
                <div className="text-slate-700 whitespace-pre-line leading-relaxed">
                  {currentJournalPage.content}
                </div>
                
                {currentJournalPage.hasWritingSpace && (
                  <div className="mt-8 space-y-4">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="border-b border-slate-200 pb-2">
                        <div className="h-6"></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Page Navigation */}
            <div className="bg-slate-50 px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  {journalPages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPage(index)}
                      className={`w-3 h-3 rounded-full cursor-pointer ${
                        index === currentPage ? 'bg-blue-800' : 'bg-slate-300'
                      }`}
                    />
                  ))}
                </div>
                <div className="text-sm text-slate-600">
                  Click dots to jump to any page
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">What's Inside The Complete Journal</h2>
            <p className="text-xl text-slate-600">100 pages of guided transformation</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                <i className="ri-edit-line text-2xl text-blue-800"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Daily Prompts</h3>
              <p className="text-slate-600">Thoughtfully crafted questions to guide your daily reflection and self-discovery.</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                <i className="ri-calendar-line text-2xl text-blue-800"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Monthly Themes</h3>
              <p className="text-slate-600">Each month focuses on a specific aspect of personal growth and development.</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                <i className="ri-lightbulb-line text-2xl text-blue-800"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Insight Tracking</h3>
              <p className="text-slate-600">Track patterns, breakthroughs, and personal insights over time.</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                <i className="ri-target-line text-2xl text-blue-800"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Goal Setting</h3>
              <p className="text-slate-600">Set meaningful goals and track your progress with structured frameworks.</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                <i className="ri-heart-line text-2xl text-blue-800"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Gratitude Practice</h3>
              <p className="text-slate-600">Cultivate appreciation and positive mindset through daily gratitude exercises.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">What Readers Are Saying</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: "This journal completely transformed how I approach my goals. The spiritual foundation combined with practical planning is exactly what I needed.",
                author: "Sarah M.",
                role: "Entrepreneur"
              },
              {
                quote: "I've tried many journals, but this one actually helped me discover my true calling. The reflection exercises are profound yet accessible.",
                author: "Michael R.",
                role: "Teacher"
              },
              {
                quote: "The Dream Journal helped me reconnect with my faith while building a clear path toward my dreams. It's become my daily companion.",
                author: "Jennifer L.",
                role: "Coach"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-lg">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="text-slate-700 mb-4 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="text-slate-600">
                  <strong>{testimonial.author}</strong> - {testimonial.role}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your Transformation?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands who have discovered their purpose and achieved their dreams through 
            guided reflection and faith-based planning.
          </p>
          <div className="flex justify-center">
            <Link to="/shop">
              <Button size="lg" className="bg-yellow-500 text-slate-800 hover:bg-yellow-400 font-semibold">
                Get The Dream Journal - $24.99
              </Button>
            </Link>
          </div>
          <p className="text-sm mt-4 opacity-75">
            Available in paperback ($24.99) and digital PDF ($14.99)
          </p>
        </div>
      </section>

      {/* Email Popup */}
      {showEmailPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-slate-800">Get Your Free Sample</h3>
              <button 
                onClick={() => setShowEmailPopup(false)}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="text-slate-600 mb-6">
              Download 10 sample pages from The Dream Journal including guided exercises, 
              planning templates, and daily reflection pages.
            </p>
            
            {submitStatus === 'success' && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm">Thank you! Check your email for your free sample pages.</p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">Something went wrong. Please try again.</p>
              </div>
            )}

            <form onSubmit={handleEmailSubmit} data-readdy-form>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <Button 
                type="submit" 
                className="w-full bg-blue-800 hover:bg-blue-900" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Download Free Sample'}
              </Button>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
